import React, { useState, useEffect } from 'react';
import { useApi } from '../../../hooks/useApi';
import { filterBySearch } from '../../../utils/search';

function AdminMarketplaceAccounts() {
  const { request, showStatus } = useApi();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [plans, setPlans] = useState([]);
  const [planModal, setPlanModal] = useState({ show: false, accountId: null, currentPlan: null });
  // Раньше грузили ВСЕ кабинеты платформы одним запросом без limit/offset —
  // при росте базы это стало бы всё медленнее и тяжелее (N+1 запросы на бэке
  // на каждый кабинет). Постраничная загрузка.
  const PAGE_SIZE = 200;
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    loadAccounts();
    // eslint-disable-next-line
  }, [page]);

  const loadPlans = async () => {
    try {
      const data = await request('/api/v1/admin/plan-catalog');
      setPlans(data || []);
    } catch (error) {
      // Fallback to defaults if endpoint fails
      setPlans([
        { key: 'free', label: 'Бесплатный', order: 1 },
        { key: 'standard', label: 'Стандартный', order: 2 },
        { key: 'full', label: 'Полный', order: 3 }
      ]);
    }
  };

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const data = await request(`/api/v1/marketplace-accounts?skip=${page * PAGE_SIZE}&limit=${PAGE_SIZE}`);
      const rows = data || [];
      setAccounts(rows);
      setHasMore(rows.length === PAGE_SIZE);
    } catch (error) {
      showStatus('error', 'Не удалось загрузить кабинеты');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlan = (accountId, currentPlan) => {
    setPlanModal({ show: true, accountId, currentPlan });
  };

  const confirmChangePlan = async (newPlan) => {
    const { accountId } = planModal;
    if (!newPlan || newPlan === planModal.currentPlan) {
      setPlanModal({ show: false, accountId: null, currentPlan: null });
      return;
    }

    try {
      await request(`/api/v1/marketplace-accounts/${accountId}/plan`, {
        method: 'POST',
        body: JSON.stringify({ new_plan: newPlan })
      });
      showStatus('success', 'Тариф изменен');
      loadAccounts();
    } catch (error) {
      showStatus('error', 'Ошибка при смене тарифа');
    }
    setPlanModal({ show: false, accountId: null, currentPlan: null });
  };

  const filtered = filterBySearch(accounts, search);

  return (
    <div className="admin-page">
      <h1>Управление кабинетами</h1>

      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="Поиск по кабинету, проекту или email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
      </div>

      <div style={{
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
            <tr>
              <th style={{ padding: '12px', textAlign: 'left' }}>Кабинет</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Проект</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Хозяин</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>Тариф</th>
              <th style={{ padding: '12px', textAlign: 'right' }}>Баланс</th>
              <th style={{ padding: '12px', textAlign: 'right' }}>Дней</th>
              <th style={{ padding: '12px', textAlign: 'right' }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="7" style={{ padding: '20px', textAlign: 'center' }}>
                  Загрузка...
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan="7" style={{ padding: '20px', textAlign: 'center' }}>
                  {accounts.length === 0 ? 'Нет кабинетов' : 'Ничего не найдено'}
                </td>
              </tr>
            )}
            {filtered.map((acc) => (
              <tr key={acc.account_id} style={{ borderBottom: '1px solid #e9ecef' }}>
                <td style={{ padding: '12px' }}>
                  <div style={{ fontWeight: 'bold' }}>{acc.account_name}</div>
                  <div style={{ fontSize: '12px', color: '#999' }}>{acc.marketplace_type.toUpperCase()}</div>
                </td>
                <td style={{ padding: '12px' }}>
                  {acc.project_name || '—'}
                </td>
                <td style={{ padding: '12px' }}>
                  <div style={{ fontWeight: 'bold' }}>
                    <a href={`/admin/users?id=${acc.owner_user_id}`} style={{ color: '#3498db', textDecoration: 'none' }}>
                      {acc.owner_email}
                    </a>
                  </div>
                  <div style={{ fontSize: '12px', color: '#a3acb9', fontFamily: 'monospace' }}>{acc.owner_user_id}</div>
                </td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    background: '#3498db',
                    color: 'white',
                    fontWeight: 'bold'
                  }}>
                    {acc.plan_type}
                  </span>
                </td>
                <td style={{ padding: '12px', textAlign: 'right' }}>
                  <div style={{ fontWeight: 'bold' }}>{acc.balance} ₽</div>
                  {acc.payer_cabinets > 1 && (
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      общий на {acc.payer_cabinets} каб.
                    </div>
                  )}
                </td>
                <td style={{ padding: '12px', textAlign: 'right' }}>
                  {acc.days_left !== null ? `${acc.days_left} дн.` : '∞'}
                </td>
                <td style={{ padding: '12px', textAlign: 'right' }}>
                  <button
                    onClick={() => handleChangePlan(acc.account_id, acc.plan_type)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#3498db',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontSize: '14px'
                    }}
                  >
                    Тариф
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 15, marginTop: 15 }}>
        <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0 || loading} style={{ padding: '8px 16px' }}>← Назад</button>
        <span style={{ color: '#666', fontSize: 14 }}>Страница {page + 1}</span>
        <button onClick={() => setPage((p) => p + 1)} disabled={!hasMore || loading} style={{ padding: '8px 16px' }}>Вперёд →</button>
      </div>

      {planModal.show && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <h3>Выберите тариф</h3>
            <div style={{ marginTop: '20px' }}>
              {(plans.length > 0 ? plans : []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((plan) => (
                <button
                  key={plan.key}
                  onClick={() => confirmChangePlan(plan.key)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '10px',
                    margin: '8px 0',
                    border: plan.key === planModal.currentPlan ? '2px solid #3498db' : '1px solid #ddd',
                    background: plan.key === planModal.currentPlan ? '#ecf0f1' : 'white',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: plan.key === planModal.currentPlan ? 'bold' : 'normal'
                  }}
                >
                  {plan.label} {plan.key === planModal.currentPlan ? '✓' : ''}
                </button>
              ))}
            </div>
            <button
              onClick={() => setPlanModal({ show: false, accountId: null, currentPlan: null })}
              style={{
                width: '100%',
                padding: '10px',
                marginTop: '15px',
                border: '1px solid #ddd',
                background: '#f8f9fa',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Отмена
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminMarketplaceAccounts;
